import React from "react";
import { useTranslation } from 'react-i18next';

import BannerWrapperCustomImg from "../top_banner/banner_wrapper_custom_img";

const EMAIL_PATTERN = /([\w.+-]+@[\w-]+\.[\w.]+[a-z])/gi;
const ALPHA = "abcdefghijklmnopqrstuvwxyz";

// Turns email addresses inside plain text into mailto links.
const RichText = ({ text }) => (
	<>
		{text.split(EMAIL_PATTERN).map((part, index) =>
			index % 2 === 1 ? (
				<a key={index} href={`mailto:${part}`} className="text-yellow-500 font-medium underline hover:text-yellow-600">
					{part}
				</a>
			) : (
				part
			)
		)}
	</>
);

const ListItems = ({ items, style }) => (
	<ul className="mt-3 space-y-2">
		{items.map((item, index) => (
			<li key={index} className="flex">
				{style === "alpha" ? (
					<span className="flex-shrink-0 w-8 font-semibold text-gray-900">({ALPHA[index]})</span>
				) : (
					<span className="flex-shrink-0 w-2 h-2 mt-2 mr-4 rounded-full bg-yellow-500" />
				)}
				<span>
					{typeof item === "string" ? (
						<RichText text={item} />
					) : (
						<>
							<span className="font-semibold text-gray-900">{item.label}</span> <RichText text={item.text} />
						</>
					)}
				</span>
			</li>
		))}
	</ul>
);

const Block = ({ block }) => {
	switch (block.type) {
		case "clause":
			return (
				<div className="flex flex-col md:flex-row">
					<span className="flex-shrink-0 md:w-14 font-bold text-yellow-500">{block.num}</span>
					<div className="flex-1">
						{block.label && <span className="font-semibold text-gray-900">{block.label} </span>}
						<RichText text={block.text} />
						{block.list && <ListItems items={block.list} style="alpha" />}
						{block.after && <p className="mt-3"><RichText text={block.after} /></p>}
					</div>
				</div>
			);
		case "list":
			return <ListItems items={block.items} style={block.style} />;
		case "definitions":
			return (
				<dl className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
					{block.items.map((item) => (
						<div key={item.term} className="flex flex-col md:flex-row px-4 py-3 md:space-x-6">
							<dt className="flex-shrink-0 md:w-1/4 font-semibold text-gray-900">{item.term}</dt>
							<dd className="flex-1 mt-1 md:mt-0">{item.text}</dd>
						</div>
					))}
				</dl>
			);
		case "table":
			return (
				<div className="overflow-x-auto border border-gray-200 rounded-lg">
					<table className="w-full text-left">
						<thead className="bg-yellow-50 text-gray-900">
							<tr>
								{block.head.map((cell) => (
									<th key={cell} className="px-4 py-3 font-semibold">{cell}</th>
								))}
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-200">
							{block.rows.map((row) => (
								<tr key={row[0]}>
									{row.map((cell, index) => (
										<td key={index} className={`px-4 py-3 ${index === 0 ? "font-medium text-gray-900" : ""}`}>{cell}</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			);
		case "note":
			return <p className="text-sm italic text-gray-500"><RichText text={block.text} /></p>;
		default:
			return <p><RichText text={block.text} /></p>;
	}
};

const SectionTitle = ({ section }) => (
	<h2 className="flex items-center text-xl md:text-2xl font-bold text-gray-900 mb-6">
		{section.number && (
			<span className="flex flex-shrink-0 items-center justify-center w-10 h-10 mr-4 rounded-full bg-yellow-50 border border-yellow-300 text-yellow-500 text-base">
				{section.number}
			</span>
		)}
		{section.title}
	</h2>
);

const TableOfContents = ({ sections }) => (
	<ol className="space-y-1 text-sm">
		{sections.map((section) => (
			<li key={section.id}>
				<a
					href={`#${section.id}`}
					className="flex py-1 pl-3 border-l-2 border-transparent text-gray-600 hover:text-gray-900 hover:border-yellow-500"
				>
					{section.number && <span className="w-6 flex-shrink-0 font-semibold text-yellow-500">{section.number}</span>}
					<span>{section.title}</span>
				</a>
			</li>
		))}
	</ol>
);

// Fields of a layout that hold structure rather than translation keys.
const STRUCTURAL_FIELDS = ["id", "number", "num", "type", "style", "highlight"];

// Replaces every translation key in a layout (see ./content) with its translated text.
const translateLayout = (node, t, field) => {
	if (typeof node === "string") return STRUCTURAL_FIELDS.includes(field) ? node : t(node);
	if (Array.isArray(node)) return node.map((item) => translateLayout(item, t, field));
	if (node && typeof node === "object") {
		return Object.fromEntries(Object.entries(node).map(([key, value]) => [key, translateLayout(value, t, key)]));
	}
	return node;
};

const LegalDocument = ({ layout }) => {
	const { t, i18n } = useTranslation();
	const document = translateLayout(layout, t);
	const isFrench = (i18n.language || "").startsWith("fr");

	return (
		<div className="font-sans">
			<BannerWrapperCustomImg
				largeBgImg={"https://d2lxkizvrhu4im.cloudfront.net/images/background-cookie-banner.png"}
				nonlargeBgImg={"https://d2lxkizvrhu4im.cloudfront.net/images/background-cookie-banner.png"}
			>
				<div className="flex flex-col justify-center items-center h-full py-20 px-4 mx-auto text-center">
					<h1 className="font-bold text-3xl md:text-5xl max-w-4xl">{document.title}</h1>
					{document.subtitle && <p className="mt-4 text-lg md:text-2xl font-medium">{document.subtitle}</p>}
					{document.updated && (
						<p className="mt-4 text-sm md:text-base font-medium opacity-90">
							{t('last_updated_legal', { date: document.updated })}
						</p>
					)}
				</div>
			</BannerWrapperCustomImg>

			<div className="mx-auto w-11/12 lg:w-5/6 my-12 lg:my-16 lg:flex lg:space-x-12">
				<aside className="lg:w-1/4 mb-10 lg:mb-0">
					<div className="lg:sticky lg:top-8">
						<details className="lg:hidden border border-gray-200 rounded-lg p-4">
							<summary className="font-semibold text-gray-900 cursor-pointer">{t('table_of_contents')}</summary>
							<div className="mt-4">
								<TableOfContents sections={document.sections} />
							</div>
						</details>
						<nav className="hidden lg:block">
							<p className="mb-4 text-xs font-semibold tracking-wider uppercase text-gray-500">{t('table_of_contents')}</p>
							<TableOfContents sections={document.sections} />
						</nav>
					</div>
				</aside>

				<article className="lg:w-3/4 text-gray-700 leading-relaxed">
					{!isFrench && (
						<div className="mb-10 p-4 rounded-lg bg-yellow-50 border-l-4 border-yellow-500 text-sm text-gray-800">
							{t('legal_document_french_notice')}
						</div>
					)}

					{document.sections.map((section, sectionIndex) => (
						<section
							key={section.id}
							id={section.id}
							style={{ scrollMarginTop: "2rem" }}
							className={
								section.highlight
									? "mb-12 p-6 md:p-8 rounded-xl bg-yellow-50 border border-yellow-200"
									: `mb-12 pb-12 ${sectionIndex < document.sections.length - 1 ? "border-b border-gray-200" : ""}`
							}
						>
							<SectionTitle section={section} />
							<div className="space-y-4">
								{section.blocks.map((block, index) => (
									<Block key={index} block={block} />
								))}
							</div>
						</section>
					))}

				</article>
			</div>
		</div>
	);
};

export default LegalDocument;
